import { useCallback, useMemo, useState } from "react";

import type { AttachmentKind } from "~/entities/chat";
import type { PendingUpload } from "~/shared/store/types";
import { useDraftChatStore } from "~/shared/store/useDraftChatStore";

import {
    computeRemainingByKind,
    countSelectedByKind,
    resolveSelectionReason,
    selectionToPendingUploads,
} from "./selection-helpers";
import type {
    PerKindCounts,
    PickerImage,
    UseAttachmentPickerParams,
    UseAttachmentPickerResult,
} from "./types";

// Тип-словарь picker'а вынесен в нейтральный `./types` (разрыв импорт-цикла с
// `selection-helpers`). Ре-экспорт сохраняет прежние импорты (barrel + прямые).
export type {
    PerKindCounts,
    PerKindLimits,
    PickerImage,
    PickerSourceItem,
    SelectionDisabledReason,
    UseAttachmentPickerParams,
    UseAttachmentPickerResult,
} from "./types";

const EMPTY_PENDING: readonly PendingUpload[] = Object.freeze([]);
const ALL_KINDS = ["image", "video", "audio"] as const satisfies readonly AttachmentKind[];

export function useAttachmentPicker({
    chatId,
    limits,
    currentCounts,
    items,
    isLoading,
    isError,
    refetch,
    hasMore,
    isLoadingMore,
    loadMore,
}: UseAttachmentPickerParams): UseAttachmentPickerResult {
    const pendingItems = useDraftChatStore((s) =>
        chatId ? (s.pendingByChat[chatId] ?? EMPTY_PENDING) : EMPTY_PENDING,
    );
    const pendingS3Keys = useMemo(() => {
        const keys = new Set<string>();
        for (const p of pendingItems) {
            if (p.s3Key) keys.add(p.s3Key);
        }
        return keys;
    }, [pendingItems]);

    const [selected, setSelected] = useState<Set<string>>(() => new Set());

    const acceptedKinds = useMemo<AttachmentKind[]>(
        () => ALL_KINDS.filter((k) => (limits[k] ?? 0) > 0),
        [limits],
    );

    // Per-kind количество УЖЕ выбранного в picker'е.
    const selectedByKind = useMemo<PerKindCounts>(
        () => countSelectedByKind(items, selected),
        [items, selected],
    );

    // Per-kind свободные слоты (после уже-в-pending + уже-выбранного-в-picker'е).
    const remainingByKind = useMemo<PerKindCounts>(
        () => computeRemainingByKind(limits, currentCounts, selectedByKind),
        [limits, currentCounts, selectedByKind],
    );

    const canSelectMoreAny = ALL_KINDS.some((k) => remainingByKind[k] > 0);

    const images = useMemo<PickerImage[]>(() => {
        return items.map((it) => {
            const alreadyAdded = pendingS3Keys.has(it.s3Key);
            const isSelected = selected.has(it.s3Key);
            return {
                ...it,
                alreadyAdded,
                isSelected,
                selectionDisabledReason: resolveSelectionReason({
                    alreadyAdded,
                    acceptsKind: acceptedKinds.includes(it.kind),
                    isSelected,
                    remainingForKind: remainingByKind[it.kind],
                }),
            };
        });
    }, [items, pendingS3Keys, selected, acceptedKinds, remainingByKind]);

    const toggle = useCallback(
        (s3Key: string) => {
            setSelected((prev) => {
                if (pendingS3Keys.has(s3Key)) return prev;
                const next = new Set(prev);
                if (next.has(s3Key)) {
                    next.delete(s3Key);
                    return next;
                }
                // Перепроверяем per-kind лимит на момент клика — соседние
                // тайлы могли уже занять слоты.
                const item = items.find((it) => it.s3Key === s3Key);
                if (!item || !acceptedKinds.includes(item.kind)) return prev;
                const alreadySelectedOfKind = countSelectedByKind(items, prev)[item.kind];
                const slotsAfter =
                    (limits[item.kind] ?? 0) - currentCounts[item.kind] - alreadySelectedOfKind;
                if (slotsAfter <= 0) return prev;
                next.add(s3Key);
                return next;
            });
        },
        [pendingS3Keys, items, acceptedKinds, limits, currentCounts],
    );

    const clear = useCallback(() => setSelected(new Set()), []);

    const commit = useCallback(
        (): PendingUpload[] => selectionToPendingUploads(items, selected),
        [items, selected],
    );

    return {
        images,
        selectedCount: selected.size,
        canSelectMoreAny,
        acceptedKinds,
        isLoading,
        isError,
        refetch,
        hasMore,
        isLoadingMore,
        loadMore,
        toggle,
        clear,
        commit,
    };
}
